import React, { useMemo } from 'react';
import { Button } from 'antd';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { useAppDispatch, useAppSelector } from 'store';

import {
  setCurrentQuestionIndex,
  setIsAnswered,
  setIsCorrectAnswer,
  setSelectedOption,
  useCheckThemedTest,
  useFinishTest,
} from 'modules/mavzuli-test';

export const ActionButton = () => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const { mutate } = useCheckThemedTest();
  const { finishTest, isLoading } = useFinishTest({});
  const isAnswered = useAppSelector(({ topicTest }) => topicTest.isAnswered);
  const ticketId = useAppSelector(({ topicTest }) => topicTest.test?.ticket_id);
  const selectedOption = useAppSelector(({ topicTest }) => topicTest.selectedOption);
  const isCorrectAnswer = useAppSelector(({ topicTest }) => topicTest.isCorrectAnswer);
  const questions = useAppSelector(({ topicTest }) => topicTest.test?.test[0]?.questions);
  const currentQuestionIndex = useAppSelector(({ topicTest }) => topicTest.currentQuestionIndex);

  const question = questions?.[currentQuestionIndex as number];
  const isLastQuestion = currentQuestionIndex === (questions || [])?.length - 1;

  const playSound = (correct: boolean) => {
    const sound = new Audio(correct ? '/sounds/correct_answer.mp3' : '/sounds/incorrect.mp3');

    sound.play();
  };

  const onSubmit = () => {
    mutate({
      testId: Number(question?.id),
      optionId: selectedOption
    });

    dispatch(setIsAnswered(true));
    dispatch(setIsCorrectAnswer(question?.is_true === selectedOption));
  };

  const onNext = () => {
    dispatch(setIsAnswered(false));
    dispatch(setCurrentQuestionIndex(currentQuestionIndex + 1));
    dispatch(setSelectedOption(null));
    dispatch(setIsCorrectAnswer(false));
  };

  const onActionClick = () => {
    if (!selectedOption) return;

    if (!isAnswered) {
      onSubmit();
      playSound(selectedOption === question?.is_true);
      return;
    }

    if (isLastQuestion) {
      finishTest(ticketId as number);
      return;
    }

    onNext();
  };

  const buttonText = useMemo(() => {
    if (isAnswered || question?.checked) {
      return currentQuestionIndex === (questions?.length || 0) - 1 ? t('yakunlash') : t('keyingisi');
    }

    return t('tekshirish');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestionIndex, isAnswered, question?.checked, questions?.length]);

  return (
    <Button
      disabled={!selectedOption}
      loading={isLoading}
      onClick={onActionClick}
      className={classNames('!rounded-lg !border-none !px-8 !py-5 !font-medium max-lg:!px-4', {
        '!bg-green-500 !text-white': selectedOption || isCorrectAnswer,
        '!bg-red-500 !text-white': selectedOption && isAnswered && !isCorrectAnswer,
        '!bg-gray-100 !text-gray-400': !isAnswered && !selectedOption
      })}
    >
      {buttonText}
    </Button>
  );
};
